import { createRouter, createWebHistory } from 'vue-router'

// Only eagerly load the homepage - everything else is lazy loaded
import Welcome from "@/Pages/Welcome.vue";

// Prefetch important routes during idle time
const prefetchRoutes = () => {
    const importantRoutes = [
        () => import("@/Pages/Subscription/Pricing.vue"),
        () => import("@/Pages/Music/Ambience.vue"),
        () => import("@/Pages/Compendium/Compendium.vue"),
        () => import("@/Pages/Workshop/Workshop.vue"),
        () => import("@/Pages/Adventures/Adventures.vue"),
        () => import("@/Pages/Campaign/Campaign.vue"),
        () => import("@/Pages/Creature/Creature.vue"),
        () => import("@/Pages/Character/Character.vue"),
    ];

    const prefetchNext = (index = 0) => {
        if (index >= importantRoutes.length) return;

        const scheduleNext = () => {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => prefetchNext(index + 1), { timeout: 3000 });
            } else {
                setTimeout(() => prefetchNext(index + 1), 500);
            }
        };

        importantRoutes[index]().then(scheduleNext).catch(scheduleNext);
    };

    // Start prefetching after a short delay to let the main page settle
    if ('requestIdleCallback' in window) {
        requestIdleCallback(() => prefetchNext(), { timeout: 5000 });
    } else {
        setTimeout(() => prefetchNext(), 2000);
    }
};

// Start prefetching when the page is fully loaded
if (typeof window !== 'undefined') {
    if (document.readyState === 'complete') {
        prefetchRoutes();
    } else {
        window.addEventListener('load', prefetchRoutes, { once: true });
    }
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'welcome',
            component: Welcome,
        },
        {
            path: '/equipment',
            name: 'equipment-stash',
            component: () => import("@/Pages/Equipment/Equipment.vue"),
        },
        {
            path: '/creature',
            name: 'creature',
            component: () => import("@/Pages/Creature/Creature.vue"),
        },
        {
            path: '/character',
            name: 'character',
            component: () => import("@/Pages/Character/Character.vue"),
        },
        {
            path: '/merchant',
            name: 'merchant',
            component: () => import("@/Pages/Npc/Merchant.vue"),
        },
        {
            path: '/party',
            name: 'party',
            component: () => import("@/Pages/Party/Party.vue"),
        },
        {
            path: '/login',
            name: 'login',
            component: () => import("@/Pages/Auth/Login.vue"),
        },
        {
            path: '/group',
            name: 'join-in',
            component: () => import("@/Pages/Party/JoinIn.vue"),
        },
        {
            path: '/subscription',
            name: 'subscription',
            component: () => import("@/Pages/Subscription/Subscription.vue"),
        },
        {
            path: '/pricing',
            name: 'pricing',
            component: () => import("@/Pages/Subscription/Pricing.vue"),
        },
        {
            path: '/history',
            name: 'history',
            component: () => import("@/Pages/History/History.vue"),
        },
        {
            path: '/join/:id',
            name: 'join-in-details',
            component: () => import("@/Pages/Party/JoinInDetails.vue"),
        },
        {
            path: '/faq',
            name: 'faq',
            component: () => import("@/Pages/BusinessPages/FaqPage.vue"),
        },
        {
            path: '/ai-assistants',
            name: 'ai-assistants',
            component: () => import("@/Pages/BusinessPages/AiAssistants.vue"),
        },
        {
            path: '/campaigns_faq',
            name: 'campaigns_faq',
            component: () => import("@/Pages/BusinessPages/CampaignFaqPage.vue"),
        },
        {
            path: '/privacy-policy',
            name: 'privacy-policy',
            component: () => import("@/Pages/BusinessPages/PrivacyPolicy.vue"),
        },
        {
            path: '/terms-and-conditions',
            name: 'terms-and-conditions',
            component: () => import("@/Pages/BusinessPages/TermsAndConditions.vue"),
        },
        {
            path: '/support',
            name: 'support',
            component: () => import("@/Pages/BusinessPages/Support.vue"),
        },
        {
            path: '/how-to-play',
            name: 'how-to-play',
            component: () => import("@/Pages/BusinessPages/Framework.vue"),
        },
        {
            path: '/mergeconflict_5-6',
            name: 'mergeconflict-5-6',
            component: () => import("@/Pages/BusinessPages/MergeConflict56.vue"),
        },
        {
            path: '/mergeconflict_7-8',
            name: 'mergeconflict-7-8',
            component: () => import("@/Pages/BusinessPages/MergeConflict78.vue"),
        },
        {
            path: '/mergeconflict_9-10',
            name: 'mergeconflict-9-10',
            component: () => import("@/Pages/BusinessPages/MergeConflict910.vue"),
        },
        {
            path: '/mergeconflict_rules',
            name: 'mergeconflict-rules',
            component: () => import("@/Pages/BusinessPages/MergeConflictRules.vue"),
        },
        {
            path: '/press',
            name: 'press',
            component: () => import("@/Pages/BusinessPages/Press.vue"),
        },
        {
            path: '/compendium',
            name: 'compendium',
            component: () => import("@/Pages/Compendium/Compendium.vue"),
        },
        {
            path: '/search',
            name: 'search',
            component: () => import("@/Pages/Search/Search.vue"),
        },
        {
            path: '/moderate',
            name: 'moderate',
            component: () => import("@/Pages/Wardens/Moderate.vue"),
        },
        {
            path: '/compose',
            name: 'compose',
            component: () => import("@/Pages/Music/Compose.vue"),
        },
        {
            path: '/ambience',
            name: 'ambience',
            component: () => import("@/Pages/Music/Ambience.vue"),
        },
        {
            path: '/synthesize',
            name: 'synthesize',
            component: () => import("@/Pages/Sound/Synthesize.vue"),
        },
        {
            path: '/favorites',
            name: 'favorites',
            component: () => import("@/Pages/Favorites/Favorites.vue"),
        },
        {
            path: '/collections',
            name: 'collections',
            component: () => import("@/Pages/Compendium/Collections.vue"),
        },
        {
            path: '/collections/:id',
            name: 'collection-detail',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/booklet',
            name: 'booklet',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/creature/history',
            name: 'creature-history',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/character/history',
            name: 'character-history',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/merchant/history',
            name: 'merchant-history',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/equipment/history',
            name: 'equipment-history',
            component: () => import("@/Pages/Compendium/Booklet.vue"),
        },
        {
            path: '/hearth',
            name: 'hearth',
            component: () => import("@/Pages/Hearth/Hearth.vue"),
        },
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: () => import("@/Pages/Auth/ForgotPassword.vue"),
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import("@/Pages/Auth/ResetPassword.vue"),
        },
        {
            path: '/verify-email',
            name: 'verify-email',
            component: () => import("@/Pages/Auth/VerifyEmail.vue"),
        },
        {
            path: '/unsubscribe',
            name: 'unsubscribe',
            component: () => import("@/Pages/Auth/UnsubscribeEmail.vue"),
        },
        {
            path: '/account',
            name: 'account',
            component: () => import("@/Pages/Auth/Account.vue"),
        },
        {
            path: '/campaign',
            name: 'campaign',
            component: () => import("@/Pages/Campaign/Campaign.vue"),
        },
        {
            path: '/campaign-journal',
            name: 'campaign-journal',
            component: () => import("@/Pages/Campaign/CampaignJournal.vue"),
        },
        {
            path: '/adventures',
            name: 'adventures',
            component: () => import("@/Pages/Adventures/Adventures.vue"),
        },
        {
            path: '/workshop',
            name: 'workshop',
            component: () => import("@/Pages/Workshop/Workshop.vue"),
        },
        {
            path: '/blog',
            name: 'blog',
            component: () => import("@/Pages/Blog/BlogList.vue"),
        },
        {
            path: '/blog/:slug',
            name: 'blog-entry',
            component: () => import("@/Pages/Blog/BlogEntry.vue"),
        },
        {
            path: '/blog-studio/:slug',
            name: 'blog-studio-edit',
            component: () => import("@/Pages/Blog/BlogStudio.vue"),
        },
        {
            path: '/blog-studio',
            name: 'blog-studio',
            component: () => import("@/Pages/Blog/BlogStudio.vue"),
        },
        {
            path: '/campaign-studio',
            name: 'campaign-studio',
            component: () => import("@/Pages/CampaignStudio/CampaignStudio.vue"),
        },
        {
            path: '/puzzle',
            name: 'puzzle',
            component: () => import("@/Pages/Puzzles/DemonBox/GridPuzzle.vue"),
        },
        {
            path: '/skill-table',
            name: 'skill-table',
            component: () => import("@/Pages/SkillTable/SkillTable.vue"),
        },
        {
            path: '/stream',
            name: 'stream-chat',
            component: () => import("@/Pages/StreamChat/StreamChat.vue"),
        },
        {
            path: '/universal-chat-demo',
            name: 'universal-chat-demo',
            component: () => import("@/Pages/UniversalChatDemo.vue"),
        },
        {
            path: '/lobby',
            name: 'lobby',
            component: () => import("@/Pages/Lobby/Lobby.vue"),
        },
        {
            path: '/tabletop',
            name: 'tabletop',
            component: () => import("@/Pages/Tabletop/TabletopPage.vue"),
        },
        {
            path: '/style-studio',
            name: 'style-studio',
            component: () => import("@/Pages/ArtStyles/StyleStudio.vue"),
        },
        {
            path: '/etch',
            redirect: '/etch/editor',
        },
        {
            path: '/etch/projects',
            name: 'etch-projects',
            component: () => import("@/Pages/Etch/EtchProjectList.vue"),
        },
        {
            path: '/etch/editor',
            name: 'etch-editor',
            component: () => import("@/Pages/Etch/EtchEditor.vue"),
        },
        {
            path: '/etch/print',
            name: 'etch-print',
            component: () => import("@/Pages/Etch/EtchPrintPreview.vue"),
        },
        {
            path: '/debug/sentry-test',
            name: 'sentry-test',
            component: () => import("@/Pages/Debug/SentryTest.vue"),
        }
    ]
})

export { router as Routes };
